/**
 * CommunicationsHubPage — Centro de Comunicaciones (Iter 123).
 *
 * Punto único para gestionar formularios públicos, emails automáticos, WhatsApp,
 * remitentes/destinatarios, variables disponibles e historial de cambios.
 *
 * Estrategia: NO duplica datos. Cada pestaña embebe la página existente y
 * añade una sección "Variables disponibles" e "Historial" que son nuevas.
 * Las rutas antiguas siguen funcionando.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ClipboardList, Mail, MessageSquare, Send, Code2, History, Copy, ShieldAlert, Lock, UserCog, MailCheck, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

import FormConfigPage from "@/pages/admin/FormConfigPage";
import EmailConfigPage from "@/pages/admin/EmailConfigPage";
import WhatsappDashboard from "@/pages/admin/WhatsappDashboard";

// Variables disponibles para plantillas de email y WhatsApp.
const AVAILABLE_VARS = [
  { name: "{name}", desc: "Nombre del cliente o usuario" },
  { name: "{client_name}", desc: "Nombre completo del cliente" },
  { name: "{phone}", desc: "Teléfono del cliente" },
  { name: "{email}", desc: "Email del cliente" },
  { name: "{plate}", desc: "Matrícula del vehículo" },
  { name: "{brand}", desc: "Marca del vehículo" },
  { name: "{model}", desc: "Modelo del vehículo" },
  { name: "{center}", desc: "Centro asignado" },
  { name: "{center_address}", desc: "Dirección del centro (calle y número)" },
  { name: "{center_city}", desc: "Ciudad del centro" },
  { name: "{center_postal_code}", desc: "Código postal del centro" },
  { name: "{center_maps_url}", desc: "Enlace de Google Maps del centro" },
  { name: "{advisor}", desc: "Asesor asignado" },
  { name: "{date}", desc: "Fecha de la cita" },
  { name: "{time}", desc: "Hora de la cita" },
  { name: "{service}", desc: "Servicio principal" },
  { name: "{services}", desc: "Lista de servicios (multiservicios)" },
  { name: "{promotion}", desc: "Título de la promoción si aplica" },
  { name: "{promotion_price}", desc: "Precio de la promoción" },
  { name: "{replacement_vehicle_needed}", desc: "Si pidió vehículo de alquiler (sí/no/consultar)" },
  { name: "{replacement_vehicle_status}", desc: "Estado interno del vehículo de alquiler" },
  { name: "{replacement_vehicle_notes}", desc: "Comentarios del cliente sobre alquiler" },
];

function VariablesTab() {
  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success(`Copiado: ${txt}`);
  };
  return (
    <div className="space-y-4" data-testid="comm-hub-variables-tab">
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-4">
          <p className="text-sm text-slate-600 mb-3">
            Estas variables se reemplazan automáticamente en plantillas de email y WhatsApp.
            Click en cualquiera para copiarla al portapapeles. Si la variable no existe en una
            cita concreta (ej: <code>{"{promotion}"}</code> en cita sin promo), se sustituye por vacío sin romper el mensaje.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {AVAILABLE_VARS.map(v => (
              <button
                key={v.name}
                onClick={() => copy(v.name)}
                data-testid={`var-${v.name.replace(/[{}]/g, '')}`}
                className="flex items-center gap-2 p-2 rounded-sm border border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-left transition-colors"
              >
                <Copy className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <code className="text-xs font-mono font-bold text-blue-700">{v.name}</code>
                  <p className="text-[10px] text-slate-500 mt-0.5">{v.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PortalEmpleadoTab({ axiosAuth, isSuperAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => {
    axiosAuth().get("/communications/portal-notifications")
      .then(r => setCfg(r.data))
      .catch(() => toast.error("Error cargando configuración"))
      .finally(() => setLoading(false));
    axiosAuth().get("/communications/portal-notifications/history?limit=20")
      .then(r => setHistory(r.data?.items || []))
      .catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!isSuperAdmin) return;
    setSaving(true);
    try {
      const res = await axiosAuth().put("/communications/portal-notifications", cfg);
      setCfg(res.data);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim() && !cfg?.manager_email) {
      toast.error("Falta email destino de prueba");
      return;
    }
    setTestSending(true);
    try {
      const res = await axiosAuth().post("/communications/portal-notifications/test",
        { to: testTo.trim() || cfg.manager_email });
      toast.success(`Email de prueba enviado a ${res.data.to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando prueba");
    } finally {
      setTestSending(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-400">Cargando...</p>;
  if (!cfg) return <p className="text-sm text-red-600">No se pudo cargar configuración.</p>;

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  return (
    <div className="space-y-4" data-testid="portal-empleado-tab">
      {!isSuperAdmin && (
        <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
          <Lock className="w-3 h-3" /> Solo super_admin puede modificar esta configuración.
        </div>
      )}

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-700">
            ¿A quién avisar cuando un empleado crea una solicitud desde Mi Portal?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: "notify_direct_manager", label: "Email al jefe directo del empleado", hint: "Usa el email del empleado marcado como jefe directo en su ficha." },
            { key: "notify_manager", label: "Email al gerente", hint: "Configurado abajo." },
            { key: "notify_hr_copy", label: "Copia a RRHH", hint: "Útil para llevar registro centralizado." },
          ].map(t => (
            <label key={t.key} className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg[t.key]}
                disabled={!isSuperAdmin}
                onChange={(e) => set(t.key, e.target.checked)}
                data-testid={`portal-notif-${t.key}`}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium text-slate-900">{t.label}</span>
                <span className="block text-[11px] text-slate-500">{t.hint}</span>
              </span>
            </label>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email gerente</label>
              <Input type="email" value={cfg.manager_email}
                onChange={(e) => set("manager_email", e.target.value)}
                disabled={!isSuperAdmin}
                placeholder="gerente@ascauto.com"
                className="rounded-sm h-9 border-slate-200 text-sm"
                data-testid="portal-notif-manager-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email RRHH</label>
              <Input type="email" value={cfg.hr_email}
                onChange={(e) => set("hr_email", e.target.value)}
                disabled={!isSuperAdmin}
                placeholder="rrhh@ascauto.com"
                className="rounded-sm h-9 border-slate-200 text-sm"
                data-testid="portal-notif-hr-email" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Asunto plantilla</label>
            <Input value={cfg.subject_template}
              onChange={(e) => set("subject_template", e.target.value)}
              disabled={!isSuperAdmin}
              className="rounded-sm h-9 border-slate-200 text-sm font-mono"
              data-testid="portal-notif-subject" />
            <p className="text-[10px] text-slate-400 mt-0.5">
              Variables: {"{employee_name}, {request_type}, {department}, {start_date}, {end_date}, {comments}, {admin_review_url}"}
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Cuerpo plantilla</label>
            <textarea value={cfg.body_template}
              onChange={(e) => set("body_template", e.target.value)}
              disabled={!isSuperAdmin}
              rows={3}
              className="w-full rounded-sm border-slate-200 border text-xs p-2 font-mono"
              data-testid="portal-notif-body" />
          </div>

          {isSuperAdmin && (
            <Button onClick={save} disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase font-semibold h-9"
              data-testid="portal-notif-save-btn">
              {saving ? "Guardando..." : "Guardar configuración"}
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-700">
            Enviar prueba
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <Input type="email" placeholder={cfg.manager_email || "destino@ejemplo.com"}
              value={testTo} onChange={(e) => setTestTo(e.target.value)}
              className="rounded-sm h-9 border-slate-200 text-sm"
              data-testid="portal-notif-test-to" />
            <Button onClick={sendTest} disabled={testSending}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm text-xs uppercase font-semibold h-9 px-4"
              data-testid="portal-notif-test-btn">
              <Send className="w-3 h-3 mr-1" />
              {testSending ? "Enviando..." : "Enviar prueba"}
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">
            Genera una solicitud ficticia y envía el email al destinatario indicado (o al email del gerente si se deja vacío).
          </p>
        </CardContent>
      </Card>

      <Card className="border-slate-200 rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-700">
            Historial de envíos ({history.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-xs text-slate-400">Sin envíos registrados todavía.</p>
          ) : (
            <ul className="space-y-1 max-h-64 overflow-y-auto" data-testid="portal-notif-history">
              {history.map(h => (
                <li key={h.id} className="text-[11px] border-b border-slate-100 py-1.5 flex items-start gap-2">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-semibold flex-shrink-0 ${
                    h.status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
                  }`}>{h.status}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700"><strong>{h.kind}</strong> → {h.to}</p>
                    <p className="text-slate-400 text-[10px] truncate">{h.subject}</p>
                  </div>
                  <span className="text-slate-400 text-[10px] flex-shrink-0">
                    {h.created_at ? new Date(h.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function HistorialTab({ axiosAuth }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [forms, sys] = await Promise.all([
          axiosAuth().get("/form-config"),
          axiosAuth().get("/communications/recent-changes").catch(() => ({ data: { items: [] } })),
        ]);
        if (!mounted) return;
        const fc = (forms.data?.forms || [])
          .filter(f => f.updated_at)
          .map(f => ({
            type: "form",
            key: f.form_id,
            label: `Formulario: ${f.form_name || f.form_id}`,
            updated_at: f.updated_at,
            updated_by: f.updated_by || "—",
            value: `dest_email = ${f.dest_email || "(vacío)"}`,
          }));
        const extra = (sys.data?.items || []);
        const all = [...fc, ...extra].sort(
          (a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")
        ).slice(0, 30);
        setItems(all);
      } catch {
        toast.error("Error cargando historial");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [axiosAuth]);

  return (
    <Card className="rounded-sm border-slate-200" data-testid="comm-hub-history-tab">
      <CardContent className="p-4">
        <p className="text-sm text-slate-600 mb-3">Últimos cambios en configuración de comunicaciones (formularios, plantillas, remitentes).</p>
        {loading ? (
          <p className="text-xs text-slate-400">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-slate-400">Sin cambios registrados.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((it, i) => (
              <div key={i} className="py-2 grid grid-cols-1 md:grid-cols-[140px_1fr_1fr] gap-2 text-xs">
                <span className="font-mono text-slate-500">{(it.updated_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-semibold text-slate-800">{it.label}</span>
                <span className="text-slate-600">
                  <span className="text-[10px] text-slate-400 mr-1">por</span>{it.updated_by}
                  <span className="block text-[10px] text-slate-400 mt-0.5 truncate font-mono">{it.value}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RemitentesTab({ axiosAuth, isSuperAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosAuth().get("/communications/senders")
      .then(r => setCfg(r.data || {}))
      .catch(() => setCfg({}));
  }, [axiosAuth]);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const isValidEmail = (e) => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const save = async () => {
    if (!isSuperAdmin) return;
    const emailFields = ["sender_email", "reply_to_email", "contact_dest_email",
                    "hr_dest_email", "appointments_dest_email", "replacement_vehicle_dest_email",
                    "admin_dest_email", "tech_alerts_dest_email"];
    for (const f of emailFields) {
      if (cfg[f] && !isValidEmail(cfg[f])) {
        toast.error(`Email inválido en ${f}: ${cfg[f]}`);
        return;
      }
    }
    setSaving(true);
    try {
      const r = await axiosAuth().put("/communications/senders", cfg);
      setCfg(r.data || cfg);
      toast.success("Remitentes guardados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <p className="text-xs text-slate-400 p-4">Cargando...</p>;

  const fields = [
    ["sender_email", "Email remitente general", "noreply@ascauto.com", true],
    ["sender_name", "Nombre remitente general", "ASCAUTO", false],
    ["reply_to_email", "Reply-to general", "ascauto@ascauto.com", true],
    ["contact_dest_email", "Email destino de Contacto", "ascauto@ascauto.com", true],
    ["hr_dest_email", "Email destino de RRHH", "ascauto@ascauto.com", true],
    ["appointments_dest_email", "Email destino de Citas", "ascauto@ascauto.com", true],
    ["replacement_vehicle_dest_email", "Email destino de Vehículos de alquiler", "ascauto@ascauto.com", true],
    ["admin_dest_email", "Email destino de Administración", "ascauto@ascauto.com", true],
    ["tech_alerts_dest_email", "Email destino de Avisos técnicos", "ascauto@ascauto.com", true],
  ];

  return (
    <Card className="rounded-sm border-slate-200" data-testid="comm-hub-remitentes-tab">
      <CardContent className="p-4 space-y-4">
        {!isSuperAdmin && (
          <div className="p-2 rounded-sm bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" /> Solo super_admin puede modificar los remitentes y destinatarios principales. Estás en modo lectura.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fields.map(([k, label, placeholder, isEmail]) => (
            <div key={k} className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
              <Input
                type={isEmail ? "email" : "text"}
                value={cfg[k] || ""}
                onChange={e => set(k, e.target.value)}
                placeholder={placeholder}
                disabled={!isSuperAdmin}
                className={"rounded-sm h-9 text-sm border-slate-200 " + (cfg[k] && isEmail && !isValidEmail(cfg[k]) ? "border-red-400" : "")}
                data-testid={`sender-${k}`}
              />
              {cfg[k] && isEmail && !isValidEmail(cfg[k]) && <p className="text-[10px] text-red-600">Email inválido</p>}
            </div>
          ))}
        </div>
        {cfg.sender_email && (
          <div className="p-2 rounded-sm bg-blue-50 border border-blue-200 text-[11px] text-blue-800 flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Si usas SMTP Microsoft 365, el remitente debe coincidir con la cuenta autenticada o un dominio permitido (<code className="font-mono">@ascauto.com</code>). El envío fallará en caso contrario.</span>
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={save} disabled={!isSuperAdmin || saving}
            className="bg-blue-600 hover:bg-blue-700 rounded-sm text-xs uppercase font-semibold" data-testid="senders-save-btn">
            <Send className="w-3 h-3 mr-1" /> {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Iter 187 (P1) — Pestaña "Auditoría de envíos"
function AuditoriaEnviosTab({ axiosAuth, isSuperAdmin }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total_sent: 0, total_failed: 0 });
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState("");
  const [successFilter, setSuccessFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (moduleFilter) params.set("module", moduleFilter);
      if (successFilter !== "all") params.set("success", successFilter === "ok" ? "true" : "false");
      if (search) params.set("q", search);
      const res = await axiosAuth().get(`/email-config/audit-sends?${params.toString()}`);
      setLogs(res.data.logs || []);
      setStats(res.data.stats || { total_sent: 0, total_failed: 0 });
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo cargar la auditoría");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { fetchLogs(); }, []);

  if (!isSuperAdmin) {
    return (
      <Card className="rounded-sm border-amber-300 bg-amber-50">
        <CardContent className="p-4 flex items-start gap-2">
          <Lock className="w-4 h-4 text-amber-600 mt-0.5" />
          <span className="text-sm text-amber-800">Esta auditoría está restringida a super-administradores.</span>
        </CardContent>
      </Card>
    );
  }

  const modules = Array.from(new Set(logs.map((l) => l.module).filter(Boolean))).sort();
  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  };

  return (
    <div className="space-y-4" data-testid="comm-hub-auditoria-envios-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="rounded-sm border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="text-[11px] uppercase font-bold tracking-wider text-emerald-700">Enviados con éxito</p>
              <p className="text-2xl font-bold text-emerald-800" data-testid="audit-stat-sent">{stats.total_sent}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <p className="text-[11px] uppercase font-bold tracking-wider text-red-700">Con error</p>
              <p className="text-2xl font-bold text-red-800" data-testid="audit-stat-failed">{stats.total_failed}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200 bg-white">
          <CardContent className="p-4 flex items-center gap-3">
            <MailCheck className="w-6 h-6 text-slate-700" />
            <div>
              <p className="text-[11px] uppercase font-bold tracking-wider text-slate-600">Mostrados</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="audit-stat-shown">{logs.length}</p>
              <p className="text-[10px] text-slate-500">de los últimos 200 envíos filtrados</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-bold tracking-wider text-slate-600">Módulo</Label>
              <select
                value={moduleFilter}
                onChange={(e) => setModuleFilter(e.target.value)}
                className="w-full text-sm rounded-sm border border-slate-300 px-2 py-1.5 bg-white"
                data-testid="audit-filter-module"
              >
                <option value="">Todos</option>
                {[...new Set([...modules, "auth", "users", "contact", "job_applications", "hr_core", "hr_core_test", "appointments"])].sort().map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-bold tracking-wider text-slate-600">Estado</Label>
              <select
                value={successFilter}
                onChange={(e) => setSuccessFilter(e.target.value)}
                className="w-full text-sm rounded-sm border border-slate-300 px-2 py-1.5 bg-white"
                data-testid="audit-filter-status"
              >
                <option value="all">Todos</option>
                <option value="ok">Solo éxito</option>
                <option value="fail">Solo error</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-bold tracking-wider text-slate-600">Buscar (destinatario / asunto)</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
                placeholder="email o palabra del asunto…"
                className="rounded-sm h-9 border-slate-300"
                data-testid="audit-filter-search"
              />
            </div>
            <Button
              onClick={fetchLogs}
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-9 text-xs uppercase tracking-wider font-semibold"
              data-testid="audit-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Cargando…" : "Actualizar"}
            </Button>
          </div>
          {error && (
            <p className="text-xs text-red-600" data-testid="audit-error">{error}</p>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-logs-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600 w-32">Fecha</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600 w-28">Módulo</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600 w-44">Evento</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600">Destinatario</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600">Asunto</th>
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600 w-24">Estado</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-slate-400 italic">
                  Sin envíos registrados con esos filtros.
                </td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">{fmtTime(l.timestamp)}</td>
                  <td className="px-3 py-2"><code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{l.module}</code></td>
                  <td className="px-3 py-2"><code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded-sm">{l.event}</code></td>
                  <td className="px-3 py-2 text-xs font-mono">{l.to}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 max-w-md truncate" title={l.subject}>{l.subject}</td>
                  <td className="px-3 py-2">
                    {l.success ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> OK
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-[11px] text-red-700 font-semibold"
                        title={l.error || "Error sin detalle"}
                      >
                        <AlertTriangle className="w-3 h-3" /> ERROR
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-500 px-1">
        Por privacidad, el cuerpo del email nunca se almacena. Esta vista lee
        de <code>db.email_audit</code> via <code>/api/email-config/audit-sends</code>.
      </p>
    </div>
  );
}


export default function CommunicationsHubPage() {
  const { axiosAuth, isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["formularios", "emails", "whatsapp", "remitentes", "portal_empleado", "variables", "historial", "auditoria-envios"];
  const initialTab = validTabs.includes(searchParams.get("tab")) ? searchParams.get("tab") : "formularios";
  const [tab, setTab] = useState(initialTab);

  const onTabChange = (v) => {
    setTab(v);
    setSearchParams({ tab: v }, { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="communications-hub-page">
      <div>
        <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Centro de Comunicaciones</h1>
        <p className="text-sm text-slate-500 mt-1">
          Punto único para gestionar formularios públicos, emails automáticos, WhatsApp, remitentes/destinatarios e historial.
          Las rutas antiguas (<code>/admin/formularios</code>, <code>/admin/email-config</code>, <code>/admin/whatsapp</code>) redirigen aquí automáticamente.
        </p>
      </div>

      <Tabs value={tab} onValueChange={onTabChange} className="space-y-4">
        <TabsList className="w-full justify-start rounded-sm overflow-x-auto" data-testid="comm-hub-tabs">
          <TabsTrigger value="formularios" className="rounded-sm text-xs uppercase" data-testid="tab-formularios">
            <ClipboardList className="w-3 h-3 mr-1" /> Formularios
          </TabsTrigger>
          <TabsTrigger value="emails" className="rounded-sm text-xs uppercase" data-testid="tab-emails">
            <Mail className="w-3 h-3 mr-1" /> Emails
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-sm text-xs uppercase" data-testid="tab-whatsapp">
            <MessageSquare className="w-3 h-3 mr-1" /> WhatsApp
          </TabsTrigger>
          <TabsTrigger value="remitentes" className="rounded-sm text-xs uppercase" data-testid="tab-remitentes">
            <Send className="w-3 h-3 mr-1" /> Remitentes
          </TabsTrigger>
          <TabsTrigger value="portal_empleado" className="rounded-sm text-xs uppercase" data-testid="tab-portal-empleado">
            <UserCog className="w-3 h-3 mr-1" /> Portal Empleado
          </TabsTrigger>
          <TabsTrigger value="variables" className="rounded-sm text-xs uppercase" data-testid="tab-variables">
            <Code2 className="w-3 h-3 mr-1" /> Variables
          </TabsTrigger>
          <TabsTrigger value="historial" className="rounded-sm text-xs uppercase" data-testid="tab-historial">
            <History className="w-3 h-3 mr-1" /> Historial
          </TabsTrigger>
          <TabsTrigger value="auditoria-envios" className="rounded-sm text-xs uppercase" data-testid="tab-auditoria-envios">
            <MailCheck className="w-3 h-3 mr-1" /> Auditoría envíos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="formularios" className="m-0">
          <FormConfigPage />
        </TabsContent>
        <TabsContent value="emails" className="m-0">
          <EmailConfigPage />
        </TabsContent>
        <TabsContent value="whatsapp" className="m-0">
          <WhatsappDashboard />
        </TabsContent>
        <TabsContent value="remitentes" className="m-0">
          <RemitentesTab axiosAuth={axiosAuth} isSuperAdmin={isSuperAdmin} />
        </TabsContent>
        <TabsContent value="portal_empleado" className="m-0">
          <PortalEmpleadoTab axiosAuth={axiosAuth} isSuperAdmin={isSuperAdmin} />
        </TabsContent>
        <TabsContent value="variables" className="m-0">
          <VariablesTab />
        </TabsContent>
        <TabsContent value="historial" className="m-0">
          <HistorialTab axiosAuth={axiosAuth} />
        </TabsContent>
        <TabsContent value="auditoria-envios" className="m-0">
          <AuditoriaEnviosTab axiosAuth={axiosAuth} isSuperAdmin={isSuperAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
